import { apiEndpoint } from "../utils/env";

async function fetchWithAuth(url, token, options = {}) {
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  headers["Content-Type"] = headers["Content-Type"] || "application/json";
  const res = await fetch(url, { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }
  if (!res.ok) {
    const errMsg =
      json && json.message
        ? json.message
        : typeof json === "string"
        ? json
        : res.statusText;
    const error = new Error(errMsg || `Request failed: ${res.status}`);
    error.code = json && json.code ? json.code : null;
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

// Deployment Configuration APIs
export async function getDeploymentConfiguration(token) {
  const url = apiEndpoint("/api/Admin/GetDeploymentConfiguration");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateDeploymentConfiguration(token, config) {
  const url = apiEndpoint("/api/Admin/UpdateDeploymentConfiguration");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

// Directory Browser API
export async function createDirectory(token, parentPath, folderName) {
  const url = apiEndpoint("/api/Admin/CreateDirectory");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ parentPath, folderName }),
  });
}

export async function browseDirectories(token, path = null) {
  const url = path
    ? apiEndpoint(`/api/Admin/BrowseDirectories?path=${encodeURIComponent(path)}`)
    : apiEndpoint("/api/Admin/BrowseDirectories");
  return fetchWithAuth(url, token, { method: "GET" });
}

// Worker Status & Monitoring APIs
export async function getWorkerStatus(token) {
  const url = apiEndpoint("/api/Admin/GetWorkerStatus");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getWorkerMetrics(token, timeRangeMinutes = 60) {
  const url = apiEndpoint(
    `/api/Admin/GetWorkerMetrics?timeRangeMinutes=${timeRangeMinutes}`
  );
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getBatchProcessingStats(token) {
  const url = apiEndpoint("/api/Admin/GetBatchProcessingStats");
  return fetchWithAuth(url, token, { method: "GET" });
}

// Storage Configuration APIs
export async function getStorageConfiguration(token) {
  const url = apiEndpoint("/api/Admin/GetStorageConfiguration");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateStorageConfiguration(token, config) {
  const url = apiEndpoint("/api/Admin/UpdateStorageConfiguration");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

// Where each registered worker will actually put a batch — the shared setting is only the
// default, and a worker with its own override does not use it. The Storage Configuration page
// shows this so a saved value can be checked against the nodes that have to obey it.
export async function getWorkerEffectiveStorage(token) {
  const url = apiEndpoint("/api/Admin/GetWorkerEffectiveStorage");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function testStorageConnection(token, config) {
  const url = apiEndpoint("/api/Admin/TestStorageConnection");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

// Processing Configuration APIs
export async function getProcessingConfiguration(token) {
  const url = apiEndpoint("/api/Admin/GetProcessingConfiguration");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateProcessingConfiguration(token, config) {
  const url = apiEndpoint("/api/Admin/UpdateProcessingConfiguration");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

// Worker Control APIs
export async function createWorker(token, workerData) {
  const url = apiEndpoint("/api/Admin/CreateWorker");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(workerData),
  });
}

export async function deployWorker(token, workerId, targetHostId = null) {
  const url = targetHostId
    ? apiEndpoint(
        `/api/Admin/DeployWorker/${workerId}?targetHostId=${targetHostId}`
      )
    : apiEndpoint(`/api/Admin/DeployWorker/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function startWorker(token, workerId, targetHostId = null) {
  const url = targetHostId
    ? apiEndpoint(
        `/api/Admin/StartWorker/${workerId}?targetHostId=${targetHostId}`
      )
    : apiEndpoint(`/api/Admin/StartWorker/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function stopWorker(token, workerId) {
  const url = apiEndpoint(`/api/Admin/StopWorker/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function pauseWorker(token, workerId) {
  const url = apiEndpoint(`/api/Admin/PauseWorker/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function makeWorkerOffline(token, workerId) {
  const url = apiEndpoint(`/api/Admin/MakeWorkerOffline/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function deleteWorker(token, workerId, removeService = false) {
  const url = apiEndpoint(
    `/api/Admin/DeleteWorker/${workerId}${removeService ? "?removeService=true" : ""}`
  );
  return fetchWithAuth(url, token, { method: "DELETE" });
}

// Ask the target host's service manager what it thinks of a service-deployed worker. Costs an SSH
// round-trip, so it is an explicit action rather than part of the polled status.
export async function checkWorkerService(token, workerId) {
  const url = apiEndpoint(`/api/Admin/CheckWorkerService/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// Unregister the service backing a worker, leaving the worker row in place.
export async function uninstallWorkerService(token, workerId) {
  const url = apiEndpoint(`/api/Admin/UninstallWorkerService/${workerId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function getWorkerLogs(token, workerId, limit = 100) {
  const url = apiEndpoint(
    `/api/Admin/GetWorkerLogs/${workerId}?limit=${limit}`
  );
  return fetchWithAuth(url, token, { method: "GET" });
}

/** Returns the raw URL for the live-log SSE endpoint (not a fetch — caller uses ReadableStream). */
export function workerLiveLogUrl(workerGuid) {
  return apiEndpoint(`/api/Admin/StreamWorkerLogs/${workerGuid}`);
}

/** Returns the raw URL for the gatherer live-log SSE endpoint. */
export function gathererLiveLogUrl(gathererGuid) {
  return apiEndpoint(`/api/Admin/StreamGathererLogs/${gathererGuid}`);
}

export async function getWorkerAppSettings(token, workerId) {
  const url = apiEndpoint(`/api/Admin/GetWorkerAppSettings/${workerId}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateWorkerAppSettings(token, workerId, settings) {
  const url = apiEndpoint(`/api/Admin/UpdateWorkerAppSettings/${workerId}`);
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

// Gatherer APIs
export async function getGathererStatus(token) {
  const url = apiEndpoint("/api/Admin/GetGathererStatus");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getGathererDeploymentConfiguration(token) {
  const url = apiEndpoint("/api/Admin/GetGathererDeploymentConfiguration");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateGathererDeploymentConfiguration(token, config) {
  const url = apiEndpoint("/api/Admin/UpdateGathererDeploymentConfiguration");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

export async function createGatherer(token, gathererData) {
  const url = apiEndpoint("/api/Admin/CreateGatherer");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(gathererData),
  });
}

export async function startGatherer(token, gathererId, targetHostId = null) {
  const url = targetHostId
    ? apiEndpoint(`/api/Admin/StartGatherer/${gathererId}?targetHostId=${targetHostId}`)
    : apiEndpoint(`/api/Admin/StartGatherer/${gathererId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// Ask the target host's service manager about a service-deployed gatherer.
export async function checkGathererService(token, gathererId) {
  const url = apiEndpoint(`/api/Admin/CheckGathererService/${gathererId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// Unregister the service backing a gatherer, leaving the gatherer in place.
export async function uninstallGathererService(token, gathererId) {
  const url = apiEndpoint(`/api/Admin/UninstallGathererService/${gathererId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function stopGatherer(token, gathererId) {
  const url = apiEndpoint(`/api/Admin/StopGatherer/${gathererId}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function deleteGatherer(token, gathererId, removeService = false) {
  const url = apiEndpoint(
    `/api/Admin/DeleteGatherer/${gathererId}${removeService ? "?removeService=true" : ""}`
  );
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function getGathererAppSettings(token, gathererId) {
  const url = apiEndpoint(`/api/Admin/GetGathererAppSettings/${gathererId}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateGathererAppSettings(token, gathererId, settings) {
  const url = apiEndpoint(`/api/Admin/UpdateGathererAppSettings/${gathererId}`);
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(settings),
  });
}
